import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";

const CARD_WIDTH = 160;
const CARD_HEIGHT = 200;
const PREVIEW_MAX_HEIGHT = 120;

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) {
    return "";
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  const units = ["KB", "MB", "GB", "TB"];
  let size = bytes / 1024;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size.toFixed(size >= 100 ? 0 : size >= 10 ? 1 : 2)} ${units[unitIndex]}`;
};

const AssetCard = ({ asset, onOpen, onDelete, onCopyUrl, onRename, isSelected = false }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const isMenuOpen = Boolean(menuAnchor);

  const previewType = useMemo(() => {
    const type = asset?.contentType || "";

    if (type.startsWith("image/")) {
      return "image";
    }

    if (type.startsWith("video/")) {
      return "video";
    }

    if (type.startsWith("audio/")) {
      return "audio";
    }

    return "generic";
  }, [asset?.contentType]);

  const handleOpenMenu = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleCopyUrl = (event) => {
    event.stopPropagation();
    handleCloseMenu();
    onCopyUrl?.(asset);
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    handleCloseMenu();
    onDelete?.(asset);
  };

  const handleRename = (event) => {
    event.stopPropagation();
    handleCloseMenu();
    onRename?.(asset);
  };

  const handleOpenAsset = () => {
    onOpen?.(asset);
  };

  return (
    <Card
      elevation={isSelected ? 4 : 0}
      sx={{
        border: 1,
        borderColor: isSelected ? "primary.main" : "divider",
        borderRadius: 3,
        overflow: "hidden",
        height: CARD_HEIGHT,
        width: CARD_WIDTH,
        flexShrink: 0,
        display: "flex",
        transition: (theme) =>
          theme.transitions.create(["transform", "box-shadow", "border-color"], {
            duration: theme.transitions.duration.shortest,
          }),
        transform: isSelected ? "translateY(-2px)" : "none",
      }}
    >
      <CardActionArea
        onClick={handleOpenAsset}
        sx={{
          flexGrow: 1,
          alignItems: "stretch",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Stack sx={{ width: "100%", height: "100%", flexGrow: 1 }}>
          <Box
            sx={{
              position: "relative",
              height: PREVIEW_MAX_HEIGHT,
              bgcolor: "background.default",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              flexGrow: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                right: 8,
                bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.85)",
                color: (theme) => (theme.palette.mode === "dark" ? "common.white" : "text.primary"),
                px: 1,
                py: 0.25,
                borderRadius: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {asset?.title || asset?.fileName || "Untitled asset"}
            </Typography>
            {previewType === "image" && asset?.url ? (
              <Box
                component="img"
                src={asset.url}
                alt={asset?.metadata?.alt || asset?.title || asset?.fileName}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : previewType === "video" && asset?.url ? (
              <Box
                component="video"
                src={asset.url}
                muted
                loop
                playsInline
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : previewType === "audio" ? (
              <Typography variant="h6" color="text.secondary">
                Audio asset
              </Typography>
            ) : (
              <Typography variant="h6" color="text.secondary">
                {asset?.contentType || "Asset"}
              </Typography>
            )}
            <Stack
              direction="row"
              spacing={1}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
              }}
            >
              <Tooltip title="More options">
                <IconButton
                  size="small"
                  onClick={handleOpenMenu}
                  aria-label="Open asset menu"
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
          <CardContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              py: 1.5,
              px: 1.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {asset?.contentType || ""}
              {asset?.size ? ` • ${formatFileSize(asset.size)}` : ""}
            </Typography>
          </CardContent>
        </Stack>
      </CardActionArea>
      <Menu anchorEl={menuAnchor} open={isMenuOpen} onClose={handleCloseMenu}>
        <MenuItem onClick={handleOpenAsset}>View details</MenuItem>
        <MenuItem onClick={handleRename}>
          <DriveFileRenameOutlineIcon fontSize="small" sx={{ mr: 1 }} />
          Rename asset
        </MenuItem>
        <MenuItem onClick={handleCopyUrl}>
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} /> Copy asset URL
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete asset
        </MenuItem>
      </Menu>
    </Card>
  );
};

export default AssetCard;
